import { Box, Flex, Spinner, Text } from '@chakra-ui/react'

import { IDTreatment360NewsAndPressReleasesModel } from 'config/common/baseModel/ida360/IDTreatment360NewsAndPressReleases'
import { IBaseView } from 'interfaces/navigationPage.interface'
import { useMemo, useState } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { Article } from 'routes/dashboard/WidgetComponents/WidgetArticle/Article'
import { useFilteredArticles } from 'routes/dashboard/WidgetComponents/WidgetArticle/useFilteredArticles'
import { ArticleFiltersT, DefaultArticleFilters } from 'utils/useArticleFilters'
import { ArticlesFilters } from 'routes/dashboard/WidgetComponents/WidgetArticle/ArticlesFilters'
import { AppBase } from 'interfaces/appBase.interface'
import { InlineLoading } from 'components'
import { getDiseaseId } from '../Response/LeftSection/Cards/Procurement'
import { ICleanResponse } from 'api/types'
import { isEqual } from 'lodash'
import MediaTagsColors from 'config/common/tagColours/MediaTagsColors'
import { GraphColors } from '../common/GraphColors'
import { assignTagColors } from './utils/generateColor'

type AirtableBaseT = AppBase

interface VirtuosoListProps {
  data: any[]
  total: number
  canFetchMore: boolean
  isFetchingNextPage: boolean
  fetchMore: () => void
  keyNews: string
  modelEndpoint: string
  airtableBase: AirtableBaseT
}

const VirtuosoList: React.FC<VirtuosoListProps> = ({
  data,
  total,
  canFetchMore,
  isFetchingNextPage,
  fetchMore,
  keyNews,
  modelEndpoint,
  airtableBase,
}) => {
  if (!data?.length && !canFetchMore) {
    return (
      <Flex w='100%' alignItems='center' justifyContent='center'>
        No alerts at the moment
      </Flex>
    )
  }
  if (!data?.length) {
    return (
      <Flex w='100%' alignItems='center' justifyContent='center'>
        <InlineLoading />
      </Flex>
    )
  }

  const uniqueTags = [...new Set(data.flatMap((item) => item.tags || []))]
  const tagsColors = assignTagColors(uniqueTags, GraphColors, MediaTagsColors)

  return (
    <Box h={'60vh'} w='full' overflow={'hidden'} mt={'5px'} mb='1rem'>
      <Virtuoso
        style={{ height: '57vh' }}
        totalCount={total}
        itemContent={(index: number) => {
          const row = data[index]
          return (
            <Article
              id={row.id}
              modelEndpoint={modelEndpoint}
              airtableBase={airtableBase}
              key={row.id}
              url={row.link as string}
              date={row.publishedAt as string}
              title={row?.titleDisplay as string}
              tags={row?.tags as string[]}
              tagsColors={tagsColors}
              countries={row.areasAlpha3}
              isKeyNews={!!row[keyNews]}
              trackMediaClick={() => {}}
            />
          )
        }}
        endReached={() => {
          if (canFetchMore && !isFetchingNextPage) {
            fetchMore()
          }
        }}
      />
      {isFetchingNextPage && (
        <Flex w='full' justifyContent={'center'} mt='4px'>
          <Spinner />
        </Flex>
      )}
    </Box>
  )
}

interface MediaCardProps {
  children: React.ReactNode
}

const MediaCard: React.FC<MediaCardProps> = ({ children }) => {
  return (
    <Box
      w='full'
      border={'1px solid'}
      borderColor='gray3'
      borderRadius='8px'
      px={5}
      pt={4}
    >
      <Flex
        mb={4}
        flexDir={'row'}
        alignItems={'center'}
        gap={4}
        justifyContent={'space-between'}
      >
        <Text minW='max-content' fontWeight={'semibold'} fontSize={'md'}>
          Latest Media
        </Text>
      </Flex>
      {children}
    </Box>
  )
}

export function getAirtableNameByDisease(disease: string): string {
  const airtableNames: Record<string, string> = {
    Mpox: 'outbreak_mpox',
    Botulism: 'outbreak_botulism',
  }
  return airtableNames[disease] || 'outbreak_mpox'
}

export function getDiseaseFilter(disease = 'Mpox') {
  return {
    diseases: [
      {
        label: disease,
        value: +getDiseaseId(disease),
        id: +getDiseaseId(disease),
      },
    ],
  }
}

export function getDefaultArticleFilters(disease: string): ArticleFiltersT {
  return {
    ...DefaultArticleFilters,
    ...getDiseaseFilter(disease),
  }
}

interface ArticleListProps {
  isLoading: boolean
  data: ICleanResponse[] | undefined
  total: number | undefined
  canFetchMore: boolean
  isFetchingNextPage: boolean
  fetchMore: () => void
  keyNews: string
  modelEndpoint: string
  airtableBase: AppBase
}

const ArticleList: React.FC<ArticleListProps> = ({
  isLoading,
  data,
  total,
  canFetchMore,
  isFetchingNextPage,
  fetchMore,
  keyNews,
  modelEndpoint,
  airtableBase,
}) => {
  if (isLoading) {
    return (
      <Flex w='100%' h='75%' alignItems='center' justifyContent='center'>
        <Spinner />
      </Flex>
    )
  }

  if (!data?.length) {
    return (
      <Flex w='100%' h='75%' alignItems='center' justifyContent='center'>
        No Media
      </Flex>
    )
  }

  return (
    <VirtuosoList
      data={data}
      total={total ?? 0}
      canFetchMore={!!canFetchMore}
      isFetchingNextPage={isFetchingNextPage}
      fetchMore={fetchMore}
      keyNews={keyNews}
      modelEndpoint={modelEndpoint}
      airtableBase={airtableBase}
    />
  )
}

const LatestMedia = ({ disease }: { disease: string }) => {
  const keyNews = 'keyNewsAlert'

  const view = {
    airtableName: getAirtableNameByDisease(disease),
    airtableBase: 'lzdb',
    name: 'Default',
  } as IBaseView<any, any>

  const model = IDTreatment360NewsAndPressReleasesModel
  const [filters, setFilters] = useState<ArticleFiltersT>(
    getDefaultArticleFilters(disease)
  )

  function filtersCB(_filters: any) {
    setFilters({ ..._filters, ...getDiseaseFilter(disease) })
  }

  const disableTracking = useMemo(
    () => isEqual(filters, getDefaultArticleFilters(disease)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disease]
  )

  const {
    data,
    canFetchMore,
    isError,
    isLoading,
    fetchMore,
    isFetchingNextPage,
  } = useFilteredArticles({
    maxItems: 10,
    model,
    view,
    filters,
    disableTracking,
  })

  const total = data?.length

  if (isError) {
    return (
      <MediaCard>
        <Flex w='100%' alignItems='center' justifyContent='center' h='75%'>
          Sorry, something went wrong loading the latest media
        </Flex>
      </MediaCard>
    )
  }

  return (
    <Box
      w='full'
      border={'1px solid'}
      borderColor='gray3'
      borderRadius='8px'
      px={5}
      pt={4}
    >
      <Flex
        mb={4}
        alignItems={'center'}
        gap={4}
        justifyContent={'space-between'}
      >
        <Text minW='max-content' fontWeight={'semibold'} fontSize={'md'}>
          Latest Media
        </Text>
        <ArticlesFilters
          filtersCB={filtersCB}
          view={view}
          availableFilters={['locations', 'candidates', 'orgs', 'tags']}
          fixedFilter={['diseases', +getDiseaseId(disease)]}
        />
      </Flex>
      <ArticleList
        isLoading={isLoading}
        data={data}
        total={total ?? 0}
        canFetchMore={!!canFetchMore}
        isFetchingNextPage={isFetchingNextPage}
        fetchMore={fetchMore}
        keyNews={keyNews}
        modelEndpoint={model.endpoint}
        airtableBase={view.airtableBase as AppBase}
      />
    </Box>
  )
}

export default LatestMedia
